import PropTypes from 'prop-types';
import { useCallback } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import {
  FontAwesomeIcon,
  CATEGORIES,
} from '@hivebrite/design-system/FontAwesomeIcon';

import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';

import { windowPopupSettings } from '../helpers/socialMediaServices';

const SocialMediaButton = ({
  url,
  title,
  social,
  label,
  shareUrl,
  vertical,
  onClick = null,
  disabled = false,
  inactive = false,
  checked = false,
}) => {
  const handleClick = useCallback(
    (event) => {
      if (disabled) {
        return;
      }

      if (onClick) {
        return onClick(event, {
          name: social,
          url,
          title,
        });
      }

      const strParam = windowPopupSettings();

      event.preventDefault();
      window.open(url, title, strParam.join(',')).focus();
    },
    [
      url,
      title,
      onClick,
      disabled,
      social,
      window.screen.height,
      window.screen.width,
    ],
  );

  // Quick fix to migrate from twitter to X, waiting to move this Component to ds-social-icon
  const isTwitter = social === 'twitter';

  return (
    <SocialMediaAnchor
      role="button"
      aria-label={__('Share on %{platform}').i__({ platform: social })}
      social={social}
      className={SocialMediaIcon[social]}
      onClick={handleClick}
      onKeyPress={onEnterOrSpacePress(handleClick)}
      data-testid={`share-${social}-${
        disabled || inactive ? 'inactive' : 'active'
      }`}
      data-url={shareUrl}
      disabled={disabled}
      inactive={inactive}
      tabIndex={disabled || inactive ? -1 : 0}
    >
      {isTwitter && (
        <FontAwesomeIcon
          color="white"
          height="18px"
          iconName="x-twitter"
          iconType={CATEGORIES.BRANDS}
        />
      )}
      <CheckIcon
        className={checked && !inactive && 'icon-checkbox__label--selected'}
      />
      {vertical && (
        <SocialMediaTitle p={2}>
          <Box as="span" onClick={handleClick}>
            {label}
          </Box>
        </SocialMediaTitle>
      )}
    </SocialMediaAnchor>
  );
};

export default SocialMediaButton;

SocialMediaButton.propTypes = {
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  inactive: PropTypes.bool,
  onClick: PropTypes.func,
};

const CheckIcon = styled.span`
  &:before {
    top: -0.5em;
    right: -0.65em;
  }
`;

const SocialMediaColor = {
  facebook: '#1877F2',
  twitter: '#000000',
  linkedin: '#007bb6',
  xing: '#005A5F',
};

const SocialMediaDarkerColor = {
  facebook: '#0044BF',
  twitter: '#000000',
  linkedin: '#004883',
  xing: '#00272C',
};

const SocialMediaIcon = {
  facebook: 'icon-circle-facebook',
  linkedin: 'icon-alpha-linkedin',
  xing: 'icon-xing',
};

const SocialMediaTitle = styled(Box)`
  position: absolute;
  left: 40px;
  right: auto;
  a {
    color: ${(props) => props.theme.colors.asphalt};
  }
`;

const SocialMediaAnchor = styled.span`
  border-radius: 3px;
  color: white;
  width: 32px;
  height: 32px;
  font-size: 1.2em;
  padding-left: ${(props) => (props.social === 'xing' ? '5px' : '')};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => SocialMediaColor[props.social]};
  &:hover,
  &:focus {
    background-color: ${(props) => SocialMediaDarkerColor[props.social]};
  }
  transition: background-color 0.2s ease;
  opacity: ${({ disabled, inactive }) => (disabled || inactive ? 0.5 : 1)};
  position: relative;
`;
